import { Injectable } from '@angular/core';

export interface ImageEnhancements {
  brightness: number; // 0-200 (100 = normal)
  contrast: number; // 0-200 (100 = normal)
  saturation: number; // 0-200 (100 = normal)
  blur: number; // 0-20 px
  grayscale: number; // 0-100%
  sepia: number; // 0-100%
  temperature: number; // -100 to 100 (warm/cool)
  tint: number; // -100 to 100 (green/magenta)
  vibrance: number; // -100 to 100
  highlights: number; // -100 to 100
  shadows: number; // -100 to 100
  midtones: number; // -100 to 100
  sharpen: number; // 0-100
  denoise: number; // 0-100 (noise reduction strength)
}

export const DEFAULT_ENHANCEMENTS: ImageEnhancements = {
  brightness: 100,
  contrast: 100,
  saturation: 100,
  blur: 0,
  grayscale: 0,
  sepia: 0,
  temperature: 0,
  tint: 0,
  vibrance: 0,
  highlights: 0,
  shadows: 0,
  midtones: 0,
  sharpen: 0,
  denoise: 0,
};

@Injectable({
  providedIn: 'root',
})
export class EnhancementService {
  /**
   * Create default enhancements object
   */
  getDefaultEnhancements(): ImageEnhancements {
    return { ...DEFAULT_ENHANCEMENTS };
  }

  /**
   * Apply pixel-level adjustments to canvas
   */
  applyPixelAdjustments(
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number,
    enhancements: ImageEnhancements,
  ): void {
    const needsPixelProcessing =
      enhancements.temperature !== 0 ||
      enhancements.tint !== 0 ||
      enhancements.vibrance !== 0 ||
      enhancements.highlights !== 0 ||
      enhancements.shadows !== 0 ||
      enhancements.midtones !== 0 ||
      enhancements.sharpen > 0 ||
      enhancements.denoise > 0;

    if (!needsPixelProcessing) return;

    const imageData = ctx.getImageData(0, 0, width, height);
    const data = imageData.data;

    // Apply adjustments pixel by pixel
    for (let i = 0; i < data.length; i += 4) {
      let r = data[i];
      let g = data[i + 1];
      let b = data[i + 2];

      // Temperature adjustment (warm/cool)
      if (enhancements.temperature !== 0) {
        const temp = enhancements.temperature / 100;
        r = this.clamp(r + temp * 30);
        b = this.clamp(b - temp * 30);
      }

      // Tint adjustment (green/magenta)
      if (enhancements.tint !== 0) {
        const tint = enhancements.tint / 100;
        g = this.clamp(g + tint * 30);
      }

      // Vibrance (smart saturation)
      if (enhancements.vibrance !== 0) {
        const avg = (r + g + b) / 3;
        const amt = (enhancements.vibrance / 100) * 0.5;
        r = this.clamp(r + (r - avg) * amt);
        g = this.clamp(g + (g - avg) * amt);
        b = this.clamp(b + (b - avg) * amt);
      }

      // Levels adjustment (highlights, shadows, midtones)
      const luminance = 0.299 * r + 0.587 * g + 0.114 * b;

      if (enhancements.highlights !== 0 && luminance > 170) {
        const factor = 1 + (enhancements.highlights / 100) * 0.5;
        r = this.clamp(r * factor);
        g = this.clamp(g * factor);
        b = this.clamp(b * factor);
      }

      if (enhancements.shadows !== 0 && luminance < 85) {
        const factor = 1 + (enhancements.shadows / 100) * 0.5;
        r = this.clamp(r * factor);
        g = this.clamp(g * factor);
        b = this.clamp(b * factor);
      }

      if (enhancements.midtones !== 0 && luminance >= 85 && luminance <= 170) {
        const factor = 1 + (enhancements.midtones / 100) * 0.3;
        r = this.clamp(r * factor);
        g = this.clamp(g * factor);
        b = this.clamp(b * factor);
      }

      data[i] = r;
      data[i + 1] = g;
      data[i + 2] = b;
    }

    // Apply denoising (before sharpening for best results)
    if (enhancements.denoise > 0) {
      this.applyDenoise(data, width, height, enhancements.denoise / 100);
    }

    // Apply sharpening
    if (enhancements.sharpen > 0) {
      this.applySharpen(data, width, height, enhancements.sharpen / 100);
    }

    ctx.putImageData(imageData, 0, 0);
  }

  /**
   * Clamp value to 0-255 range
   */
  clamp(value: number): number {
    return Math.max(0, Math.min(255, value));
  }

  /**
   * Apply noise reduction using a simple box blur approach
   * Blends original pixel with averaged neighbors based on amount
   */
  applyDenoise(
    data: Uint8ClampedArray,
    width: number,
    height: number,
    amount: number,
  ): void {
    const copy = new Uint8ClampedArray(data);
    const radius = Math.max(1, Math.round(amount * 2)); // 1-2 pixel radius based on strength

    for (let y = radius; y < height - radius; y++) {
      for (let x = radius; x < width - radius; x++) {
        const idx = (y * width + x) * 4;

        let sumR = 0, sumG = 0, sumB = 0;
        let count = 0;

        // Sample neighboring pixels
        for (let dy = -radius; dy <= radius; dy++) {
          for (let dx = -radius; dx <= radius; dx++) {
            const nIdx = ((y + dy) * width + (x + dx)) * 4;
            sumR += copy[nIdx];
            sumG += copy[nIdx + 1];
            sumB += copy[nIdx + 2];
            count++;
          }
        }

        // Average the neighbors
        const avgR = sumR / count;
        const avgG = sumG / count;
        const avgB = sumB / count;

        // Blend original with averaged based on amount (preserve some detail)
        data[idx] = this.clamp(copy[idx] * (1 - amount) + avgR * amount);
        data[idx + 1] = this.clamp(copy[idx + 1] * (1 - amount) + avgG * amount);
        data[idx + 2] = this.clamp(copy[idx + 2] * (1 - amount) + avgB * amount);
      }
    }
  }

  /**
   * Apply sharpening convolution kernel
   */
  applySharpen(
    data: Uint8ClampedArray,
    width: number,
    height: number,
    amount: number,
  ): void {
    const strength = amount * 0.5;
    const kernel = [
      0,
      -strength,
      0,
      -strength,
      1 + 4 * strength,
      -strength,
      0,
      -strength,
      0,
    ];
    this.applyConvolution(data, width, height, kernel, 3);
  }

  /**
   * Apply convolution kernel to image data
   */
  applyConvolution(
    data: Uint8ClampedArray,
    width: number,
    height: number,
    kernel: number[],
    kernelSize: number,
  ): void {
    const halfSize = Math.floor(kernelSize / 2);
    const copy = new Uint8ClampedArray(data);

    for (let y = halfSize; y < height - halfSize; y++) {
      for (let x = halfSize; x < width - halfSize; x++) {
        let r = 0,
          g = 0,
          b = 0;

        for (let ky = 0; ky < kernelSize; ky++) {
          for (let kx = 0; kx < kernelSize; kx++) {
            const idx = ((y + ky - halfSize) * width + (x + kx - halfSize)) * 4;
            const k = kernel[ky * kernelSize + kx];
            r += copy[idx] * k;
            g += copy[idx + 1] * k;
            b += copy[idx + 2] * k;
          }
        }

        const idx = (y * width + x) * 4;
        data[idx] = this.clamp(r);
        data[idx + 1] = this.clamp(g);
        data[idx + 2] = this.clamp(b);
      }
    }
  }

  /**
   * Build CSS filter string from enhancements
   */
  buildFilterString(enhancements: ImageEnhancements): string {
    const filters = [];

    if (enhancements.brightness !== 100) {
      filters.push(`brightness(${enhancements.brightness}%)`);
    }
    if (enhancements.contrast !== 100) {
      filters.push(`contrast(${enhancements.contrast}%)`);
    }
    if (enhancements.saturation !== 100) {
      filters.push(`saturate(${enhancements.saturation}%)`);
    }
    if (enhancements.blur > 0) {
      filters.push(`blur(${enhancements.blur}px)`);
    }
    if (enhancements.grayscale > 0) {
      filters.push(`grayscale(${enhancements.grayscale}%)`);
    }
    if (enhancements.sepia > 0) {
      filters.push(`sepia(${enhancements.sepia}%)`);
    }

    return filters.length > 0 ? filters.join(' ') : 'none';
  }

  /**
   * Check if any enhancements are active
   */
  hasActiveEnhancements(enhancements: ImageEnhancements): boolean {
    return (
      enhancements.brightness !== 100 ||
      enhancements.contrast !== 100 ||
      enhancements.saturation !== 100 ||
      enhancements.blur > 0 ||
      enhancements.grayscale > 0 ||
      enhancements.sepia > 0 ||
      enhancements.temperature !== 0 ||
      enhancements.tint !== 0 ||
      enhancements.vibrance !== 0 ||
      enhancements.highlights !== 0 ||
      enhancements.shadows !== 0 ||
      enhancements.midtones !== 0 ||
      enhancements.sharpen > 0 ||
      enhancements.denoise > 0
    );
  }

  /**
   * Reset all enhancements to defaults
   */
  resetEnhancements(): ImageEnhancements {
    return this.getDefaultEnhancements();
  }
}
